import { useState, useContext } from "react";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  OutlinedInput,
  Typography,
  Dialog,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { appLogger, Http } from "../../../utils";
import { UserContext } from "../../../context/userContext";
import { UserLoginInfo } from "../../../types/context";

import { withSnackbar } from "../../../utils/common/SnackbarHoc";

interface IProps {
  open: boolean;
  onClose: () => void;
  handleFetchStoredDocuments: () => void;
  selectedFolder: any;
  snackbarShowMessage: any;
  isChatGPTAPIKeyValid?: boolean;
}

const ImportWebsite = ({
  open,
  onClose,
  handleFetchStoredDocuments,
  selectedFolder,
  snackbarShowMessage,
  isChatGPTAPIKeyValid,
}: IProps) => {
  const { productId, versionId } = useParams();
  const { userInfo } = useContext(UserContext) as UserLoginInfo;

  const [state, setState] = useState({ knowledgebaseUrl: "" });
  const [invalidUrl, setInvalidUrl] = useState(false);

  const handleImportWebsite = async () => {
    if (!isChatGPTAPIKeyValid) {
      snackbarShowMessage(
        "Please provide a valid ChatGPT API key in Chat configuration under AI Settings",
        "error",
        null
      );
      return;
    }

    try {
      const res: any = await Http.post({
        url: `/api/v2/kbArticles/url`,
        state: userInfo,
        body: {
          productId: productId,
          productVersion: versionId,
          folderName: selectedFolder.id,
          url: state.knowledgebaseUrl,
        },
      });

      if (res?.status === "Url already exists") {
        snackbarShowMessage("Url already exists", "error", 3000);
        return;
      }

      if (res) {
        handleFetchStoredDocuments();
        onClose();
      }
    } catch (err) {
      appLogger.error({
        infoObj: {
          method: "handleImportWebsite",
          component: "ImportWebsite",
          fileName: "ImportWebsite.tsx",
        },
      });
      onClose();
    }
  };

  function isValidURL(url: string) {
    const urlPattern =
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/;

    return urlPattern.test(url);
  }

  const handleOnChange = (e: any) => {
    setInvalidUrl(false);
    const { name, value } = e.target;
    if (name === "knowledgebaseUrl") {
      if (!isValidURL(value)) {
        setInvalidUrl(true);
      }
    }
    setState({ ...state, [name]: value });
  };

  const shouldDisableImport = () => {
    const { knowledgebaseUrl } = state;

    if (knowledgebaseUrl && invalidUrl) {
      return false;
    }

    if (knowledgebaseUrl) {
      return false;
    }

    return true;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <Grid
        container
        p={3}
        sx={{
          width: "700px",
          maxWidth: "700px",
          margin: "0 auto",
          border: "1px dashed #3C404333",
          borderRadius: "14px",
        }}
      >
        <Grid item xs={12} md={6}>
          <Typography
            sx={{
              fontWeight: 600,
            }}
          >
            Import from website
          </Typography>
        </Grid>

        <Grid item xs={12} mt={2}>
          <FormControl className="custom-select" fullWidth>
            <InputLabel>Knowledgebase url</InputLabel>
            <OutlinedInput
              type="text"
              name="knowledgebaseUrl"
              value={state.knowledgebaseUrl}
              onChange={handleOnChange}
              id="kburl-select"
              label="kbUrl"
            />
            {invalidUrl && (
              <Typography
                sx={{ fontSize: "12px", color: "red", mt: 0.5, ml: 0.25 }}
              >
                Please enter a valid url
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid
          container
          alignItems="center"
          columnGap="15px"
          item
          xs={12}
          mt={2}
          justifyContent="end"
        >
          <Button
            variant="contained"
            disabled={shouldDisableImport()}
            onClick={handleImportWebsite}
            sx={{ fontSize: "12px", lineHeight: 1, padding: "11px 12px" }}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ borderRadius: "4px !important" }}
          >
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default withSnackbar(ImportWebsite);
